import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Message, ThreadSummary } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import {
    addQuotedMessage,
    createReaction,
    deleteReaction,
    openSelectedThreadMessage,
} from 'flow/actions/chat-actions';
import { useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useThreadView } from 'flow/contexts/MessageThreadCheckProvider';
import ChatMessageDropdownMenu, {
    getDropdownMenuControls,
    hasDropdownMenuControls,
} from 'flow/pages/messages/ChatMessageDropdownMenu';
import ReactionPicker from './reactions/ReactionPicker';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
    showAvatar: boolean;
    showReactions: boolean;
    showContextMenu: boolean;
    /** Shortcodes of the reactions the own user has put on this message. */
    myReactions: string[];
    threadSummary: TypedMap<ThreadSummary>;
    /** The thread a reaction belongs to, or null outside a thread. */
    threadParentKey: string | null;
    videoDark: boolean;
}

/**
 * The controls revealed at the end of a message on hover or focus: the quick
 * reactions, the emoji picker and the more menu. A message with none of them
 * has no bar. In Chromium 150 and later the browser's focusgroup makes the
 * bar one Tab stop, with ArrowLeft, ArrowRight, Home and End moving between
 * its shown buttons. Older browsers ignore the attribute and keep each button
 * in the Tab order. The message list keeps the bars of every other message
 * out of the Tab order.
 */
const ChatMessageActionBar = ({
    message,
    sessionKey,
    showAvatar,
    showReactions,
    showContextMenu,
    myReactions,
    threadSummary,
    threadParentKey,
    videoDark,
}: Props) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const { hasFeature } = useFeatureFlags();
    const isThreadView = useThreadView();

    const showReactionPicker =
        hasFeature(FeatureFlags.REACTIONS) &&
        showReactions &&
        !message.get('alsoAsSMS');
    const dropdownMenuControls = getDropdownMenuControls({
        hasFeature,
        message,
        showContextMenu,
        isThreadEnabled: !!hasFeature(FeatureFlags.THREADS),
        showReactions,
    });
    const showDropdownMenu =
        !isThreadView && hasDropdownMenuControls(dropdownMenuControls);
    // A bar with no control renders nothing: a named toolbar is announced
    // even when it is empty, and hover would show an empty box.
    if (!showReactionPicker && !showDropdownMenu) return null;

    const toggleReaction = (emoji: string) => {
        const method = myReactions.includes(emoji)
            ? deleteReaction
            : createReaction;

        dispatch(
            method({
                chatSessionKey: sessionKey,
                chatMessageKey: message.get('key'),
                shortcode: emoji,
                threadParentKey,
            }),
        );
    };

    return (
        <div
            // `inline` keeps ArrowUp and ArrowDown out of the bar: the message
            // list moves between messages with them, and at the oldest
            // message ArrowUp scrolls, which loads older ones.
            focusgroup="toolbar inline wrap"
            role="toolbar"
            aria-label={i18next.t('message.input.actions')}
            className={twMerge(
                'no-select shadow-base reveal-on-hover-or-focus absolute end-0 z-20 flex items-center rounded-sm p-1',
                videoDark ? 'bg-gray-700' : 'bg-white',
                hasFeature([
                    FeatureFlags.REACTIONS,
                    FeatureFlags.QUOTED_REPLIES,
                ]) && showAvatar
                    ? '-top-4'
                    : '-top-8',
            )}
        >
            {showReactionPicker && (
                <ReactionPicker
                    myReactions={myReactions}
                    videoDark={videoDark}
                    onSelect={toggleReaction}
                />
            )}
            {showDropdownMenu && (
                <ChatMessageDropdownMenu
                    controls={dropdownMenuControls}
                    message={message}
                    sessionKey={sessionKey}
                    openSelectedThreadMessage={openSelectedThreadMessage}
                    addQuotedMessage={addQuotedMessage}
                    threadSummary={threadSummary}
                    videoDark={videoDark}
                />
            )}
        </div>
    );
};

export default ChatMessageActionBar;
